/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {mat4, vec3, vec4} from "gl-matrix";
import BoundingBox from "App/utils/bounding_box";

export let FrustumVolumePlaneEquations: vec4[] = [
    vec4.create(),
    vec4.create(),
    vec4.create(),
    vec4.create(),
    vec4.create(),
    vec4.create()
];

export let pgcTempVec4: vec4 = vec4.create();
export let pgcTempVec3: vec3 = vec3.create();

export class FrustumVolume {

    private planeNormals: vec4[] = [];
    private planeDistances: number[] = [0, 0, 0, 0, 0, 0, 0];
    private directionIndex: number[] = [0, 0, 0, 0, 0, 0, 0];

    constructor(glmClipMat: mat4)
    {
        if (arguments.length === 0) {
            return;
        }

        for (let i = 0; i < 6; i++) {
            this.planeNormals.push(vec4.create());
        }

        FrustumVolumePlaneEquations[0][0] = glmClipMat[3]  - glmClipMat[0];
        FrustumVolumePlaneEquations[0][1] = glmClipMat[7]  - glmClipMat[4];
        FrustumVolumePlaneEquations[0][2] = glmClipMat[11] - glmClipMat[8];
        FrustumVolumePlaneEquations[0][3] = glmClipMat[15] - glmClipMat[12];

        FrustumVolumePlaneEquations[1][0] = glmClipMat[3]  + glmClipMat[0];
        FrustumVolumePlaneEquations[1][1] = glmClipMat[7]  + glmClipMat[4];
        FrustumVolumePlaneEquations[1][2] = glmClipMat[11] + glmClipMat[8];
        FrustumVolumePlaneEquations[1][3] = glmClipMat[15] + glmClipMat[12];

        FrustumVolumePlaneEquations[2][0] = glmClipMat[3]  + glmClipMat[1];
        FrustumVolumePlaneEquations[2][1] = glmClipMat[7]  + glmClipMat[5];
        FrustumVolumePlaneEquations[2][2] = glmClipMat[11] + glmClipMat[9];
        FrustumVolumePlaneEquations[2][3] = glmClipMat[15] + glmClipMat[13];

        FrustumVolumePlaneEquations[3][0] = glmClipMat[3]  - glmClipMat[1];
        FrustumVolumePlaneEquations[3][1] = glmClipMat[7]  - glmClipMat[5];
        FrustumVolumePlaneEquations[3][2] = glmClipMat[11] - glmClipMat[9];
        FrustumVolumePlaneEquations[3][3] = glmClipMat[15] - glmClipMat[13];

        FrustumVolumePlaneEquations[4][0] = glmClipMat[3]  + glmClipMat[2];
        FrustumVolumePlaneEquations[4][1] = glmClipMat[7]  + glmClipMat[6];
        FrustumVolumePlaneEquations[4][2] = glmClipMat[11] + glmClipMat[10];
        FrustumVolumePlaneEquations[4][3] = glmClipMat[15] + glmClipMat[14];

        FrustumVolumePlaneEquations[5][0] = glmClipMat[3]  - glmClipMat[2];
        FrustumVolumePlaneEquations[5][1] = glmClipMat[7]  - glmClipMat[6];
        FrustumVolumePlaneEquations[5][2] = glmClipMat[11] - glmClipMat[10];
        FrustumVolumePlaneEquations[5][3] = glmClipMat[15] - glmClipMat[14];

        let vectorLength;
        for (let i = 0; i < 6; i++) {
            vectorLength = Math.sqrt(
                FrustumVolumePlaneEquations[i][0] * FrustumVolumePlaneEquations[i][0] +
                   FrustumVolumePlaneEquations[i][1] * FrustumVolumePlaneEquations[i][1] +
                   FrustumVolumePlaneEquations[i][2] * FrustumVolumePlaneEquations[i][2]
            );

            FrustumVolumePlaneEquations[i][0] /=  vectorLength;
            FrustumVolumePlaneEquations[i][1] /=  vectorLength;
            FrustumVolumePlaneEquations[i][2] /=  vectorLength;
            FrustumVolumePlaneEquations[i][3] /= -vectorLength;
        }

        // right
        vec4.copy(this.planeNormals[3], FrustumVolumePlaneEquations[0]);
        this.planeDistances[3] = FrustumVolumePlaneEquations[0][3];
        this.directionIndex[3] = FrustumVolume.updateDirectionIndex(this.planeNormals[3]);

        // left
        vec4.copy(this.planeNormals[2], FrustumVolumePlaneEquations[1]);
        this.planeDistances[2] = FrustumVolumePlaneEquations[1][3];
        this.directionIndex[2] = FrustumVolume.updateDirectionIndex(this.planeNormals[2]);

        // bottom
        vec4.copy(this.planeNormals[5], FrustumVolumePlaneEquations[2]);
        this.planeDistances[5] = FrustumVolumePlaneEquations[2][3];
        this.directionIndex[5] = FrustumVolume.updateDirectionIndex(this.planeNormals[5]);

        // top
        vec4.copy(this.planeNormals[4], FrustumVolumePlaneEquations[3]);
        this.planeDistances[4] = FrustumVolumePlaneEquations[3][3];
        this.directionIndex[4] = FrustumVolume.updateDirectionIndex(this.planeNormals[4]);

        // near
        vec4.copy(this.planeNormals[0], FrustumVolumePlaneEquations[4]);
        this.planeDistances[0] = FrustumVolumePlaneEquations[4][3];
        this.directionIndex[0] = FrustumVolume.updateDirectionIndex(this.planeNormals[0]);

        // far
        vec4.copy(this.planeNormals[1], FrustumVolumePlaneEquations[5]);
        this.planeDistances[1] = FrustumVolumePlaneEquations[5][3];
        this.directionIndex[1] = FrustumVolume.updateDirectionIndex(this.planeNormals[1]);
    }

    private static updateDirectionIndex(normalVec: vec4): number {
        let ind = 0;
        if (normalVec[0] > 0) {
            ind |= 1;
        }

        if (normalVec[1] > 0) {
            ind |= 2;
        }

        if (normalVec[2] > 0) {
            ind |= 4;
        }
        return ind;
    }

    /**
     *  Check the BBox against the frustum.
     *
     * @param {BBix} aBBox tested bounding box
     * @param {int} planeMask mask which defines the used planes in the testing process
     * @returns return the mask which contains the planes which the bbox intersect
     */
    public intersect = function(aBBox: BoundingBox, planeMask: number): number
    {
        if (this.planeNormals.length < 6) {
            return -1;
        }

        //Check each point of the box to the 6 planes
        let mask = 1;
        if (planeMask < 0) {
            planeMask = 0;
        }

        for (let i = 0; i < 6; i++, mask <<= 1) {
            if ((planeMask & mask) !== 0) {
                continue;
            }

            if (this.isOutHalfSpace(aBBox, i)) {
                return -1;
            }

            if (this.isInHalfSpace(aBBox, i)) {
                planeMask |= mask;
            }
        }

        return planeMask;
    }

    //Check if the point is in the halfspace
    private pntIsInHalfSpace(i:  number, pnt: vec3): boolean {
        // @ts-ignore
        let s = vec3.dot(this.planeNormals[i], pnt) - this.planeDistances[i];
        return (s >= 0);
    }

    //Check if the box formed by min/max is fully inside the halfspace
    private isInHalfSpace(aBBox: BoundingBox, i: number): boolean {
        let p = FrustumVolume.setDirectionIndexPoint(aBBox, this.directionIndex[i]);
        return this.pntIsInHalfSpace(i, p);
    }

    //Check if the box formed by min/max is fully outside the halfspace
    private isOutHalfSpace(aBBox: BoundingBox, i: number): boolean {
        let p = FrustumVolume.setDirectionIndexPoint(aBBox, this.directionIndex[i] ^ 7);
        return !this.pntIsInHalfSpace(i, p);
    }

    private static setDirectionIndexPoint(aBBox: BoundingBox, index: number): vec3 {

        let min = vec3.clone(aBBox.getMin()), max = vec3.clone(aBBox.getMax());
        let pnt = pgcTempVec3;

        vec3.set(pnt, 0, 0, 0);
        if (index & 1) {
            pnt[0] = min[0];
        }
        else {
            pnt[0] = max[0];
        }

        if (index & 2) {
            pnt[1] = min[1];
        }
        else {
            pnt[1] = max[1];
        }

        if (index & 4) {
            pnt[2] = min[2];
        }
        else {
            pnt[2] = max[2];
        }

        return pnt;
    }

    // !static method so no prototype is here!
    public static containsPoint(aViewProjectionMatrix: mat4, aPoint: vec3): boolean
    {
        let pclip = pgcTempVec4;

        // http://stackoverflow.com/questions/6301085/how-to-check-if-an-object-lies-outside-the-clipping-volume-in-opengl
        vec4.set(pclip, aPoint[0], aPoint[1], aPoint[2], 1.0);
        vec4.transformMat4(pclip, pclip, aViewProjectionMatrix);

        return Math.abs(pclip[0]) < pclip[3] &&
            Math.abs(pclip[1]) < pclip[3] &&
            0 < pclip[2] && pclip[2] < pclip[3];
    }
}
